/* eslint-disable i18next/no-literal-string -- type definitions */
import type {UUID} from '@/types/common'
import type {AttributeValue} from '@/types/attribute'
import type {Identity, Identifier} from '@/types/identity'
import {AppLocalDate} from '@/lib/date-utils'

/**
 * Wizard step index (0-indexed).
 */
export const OnboardingStep = {
    PERSON: 0,
    ORGANIZATION: 1,
    IDENTITY: 2,
    REVIEW: 3,
} as const
export type OnboardingStep = typeof OnboardingStep[keyof typeof OnboardingStep]

/**
 * Template presets — frontend-only (no backend entity).
 */
export interface OnboardingTemplate {
    key: string
    identityType: string
    partyType: string
    showExitDate: boolean
    showIdentityType: boolean
}

export const TEMPLATES: OnboardingTemplate[] = [
    {key: 'employee', identityType: 'EMPLOYEE', partyType: 'PERSON', showExitDate: false, showIdentityType: false},
    {key: 'contractor', identityType: 'CONTRACTOR', partyType: 'PERSON', showExitDate: true, showIdentityType: false},
    {key: 'service', identityType: 'SERVICE', partyType: 'PERSON', showExitDate: false, showIdentityType: false},
    {key: 'custom', identityType: '', partyType: 'PERSON', showExitDate: false, showIdentityType: true},
]

/**
 * A row in the identifier section of Step 3.
 */
export interface IdentifierFormRow {
    id: string // local unique key for React rendering
    identifierType: string
    identifierValue: string
    primary: boolean
    fixed: boolean // true for email row (cannot be removed)
}

/**
 * Attribute value for the onboarding form.
 */
export interface SetAttributeFormValue {
    attributeName: string
    valueType: string
    valueString?: string | null
    valueNumber?: number | null
    valueBoolean?: boolean | null
    valueDate?: string | null
    valueTimestamp?: string | null
    valueJson?: unknown
    values?: unknown[]
}

/**
 * A relationship assignment in the form state.
 *
 * <p>{@code targetId} carries the target's id; for {@code MANAGED_BY} the
 * target is an Identity (epic 986 Phase D — onboarding migration to
 * IdentityRelationship), for other relationship types it's a Party. The
 * previous {@code targetPartyId} field was renamed to drop the "party"
 * connotation in the multi-employment world.
 */
export interface RelationshipFormEntry {
    relationshipType: string
    targetId: UUID | null
    targetLabel: string
    /** Secondary label — Party display name when the target is a multi-employment Identity. */
    secondaryLabel?: string
}

/**
 * Complete form state across all 4 wizard steps.
 */
export interface OnboardingFormState {
    // Step 1 — Person
    mode: 'new' | 'existing'
    templateKey: string
    existingPartyId: UUID | null
    existingPartyVersion: number | null
    displayName: string
    partyType: string

    // Step 2 — Organization
    relationships: RelationshipFormEntry[]
    partyAttributes: SetAttributeFormValue[]

    // Step 3 — Identity
    identityType: string
    entryDate: string
    exitDate: string
    identifiers: IdentifierFormRow[]
    identityAttributes: SetAttributeFormValue[]

    // Existing party data (for diff in review)
    existingPartyData: {
        displayName: string
        relationships: RelationshipFormEntry[]
        partyAttributes: AttributeValue[]
        identities: Identity[]
        identifiers: Identifier[]
    } | null

    // Meta
    dirty: boolean
    templateSwitched: boolean
}

/**
 * API request shape for POST /api/i/onboarding.
 */
export interface OnboardingRequest {
    partyId: UUID | null
    partyVersion: number | null
    partyType: string | null
    displayName: string | null
    relationships: Array<{
        relationshipType: string
        targetIdentityId: UUID
    }>
    partyAttributes: SetAttributeFormValue[]
    identityType: string
    entryDate: string | null
    exitDate: string | null
    identityAttributes: SetAttributeFormValue[]
    identifiers: Array<{
        identifierType: string
        identifierValue: string
        primary: boolean
    }>
}

/**
 * API response shape from POST /api/i/onboarding.
 */
export interface OnboardingResponse {
    partyId: UUID
    partyCreated: boolean
    identityId: UUID
    identityType: string
    lifecycleState: string
    identifierIds: UUID[]
    partyAttributesSet: number
    identityAttributesSet: number
    relationshipIds: Record<string, UUID>
}

/**
 * Duplicate check response for party name.
 */
export interface PartyNameCheckResult {
    exists: boolean
    partyType: string | null
}

/**
 * Duplicate check response for identifier.
 */
export interface IdentifierCheckResult {
    exists: boolean
}

/**
 * Initial form state factory.
 */
export function createInitialFormState(): OnboardingFormState {
    return {
        mode: 'new',
        templateKey: 'employee',
        existingPartyId: null,
        existingPartyVersion: null,
        displayName: '',
        partyType: 'PERSON',
        relationships: [],
        partyAttributes: [],
        identityType: 'EMPLOYEE',
        entryDate: AppLocalDate.today(),
        exitDate: '',
        identifiers: [
            {id: 'email-0', identifierType: 'EMAIL', identifierValue: '', primary: true, fixed: true},
        ],
        identityAttributes: [],
        existingPartyData: null,
        dirty: false,
        templateSwitched: false,
    }
}
/* eslint-enable i18next/no-literal-string */
